import { addDays, format } from 'date-fns';
import { BusinessHour, Holiday, SpecialHours } from '../types';

/**
 * Which calendar days a shop is open, derived from its settings.
 *
 * These are pure functions taking the settings explicitly, rather than methods
 * closing over React state, because the caller that needs them most — the effect
 * that has just fetched the settings — cannot see its own `setShopSettings`
 * until the next render. Reading state there meant every date looked open on
 * first load, so the picker would happily land on a Sunday.
 *
 * The API's `available-slots` endpoint remains the authority on bookable times;
 * this only decides which days the picker offers.
 */

export interface AvailabilitySettings {
  holidays?: Holiday[] | null;
  businessHours?: Record<string, BusinessHour> | null;
  specialHours?: SpecialHours[] | null;
}

type Settings = AvailabilitySettings | null | undefined;

const dateKey = (date: Date) => format(date, 'yyyy-MM-dd');

export function isHolidayOn(date: Date, settings: Settings): boolean {
  if (!settings?.holidays) return false;
  return settings.holidays.some((holiday) => holiday.date === dateKey(date));
}

export function isClosedDayOn(date: Date, settings: Settings): boolean {
  if (!settings?.businessHours) return false;

  const dayOfWeek = format(date, 'EEEE').toUpperCase();
  const dayHours = settings.businessHours[dayOfWeek];

  return !dayHours || (!dayHours.open && !dayHours.close);
}

export function getSpecialHoursOn(
  date: Date,
  settings: Settings
): SpecialHours | undefined {
  if (!settings?.specialHours) return undefined;
  return settings.specialHours.find((special) => special.date === dateKey(date));
}

/**
 * With no settings loaded every day reads as open — deliberate, so an anonymous
 * visitor who cannot read shop settings still gets a usable picker and the
 * slots endpoint does the real filtering.
 */
export function isDateOpen(date: Date, settings: Settings): boolean {
  // Order matters, and mirrors the backend's getAvailableTimeSlots:
  //   1. a holiday closes the day outright
  //   2. special hours, when present, *replace* the regular hours
  //   3. otherwise the day's business hours decide
  //
  // Checking business hours before special hours — as this did — meant a shop
  // opening specially on a Saturday had bookable slots on the server that the
  // date picker refused to offer.
  if (isHolidayOn(date, settings)) return false;

  const special = getSpecialHoursOn(date, settings);
  if (special) {
    return Boolean(special.open && special.close);
  }

  return !isClosedDayOn(date, settings);
}

/** The first open day from `startDate`, or `startDate` if none in range. */
export function findNextOpenDate(
  startDate: Date,
  settings: Settings,
  daysToCheck = 14
): Date {
  let candidate = new Date(startDate);

  for (let i = 0; i < daysToCheck; i += 1) {
    if (isDateOpen(candidate, settings)) {
      return candidate;
    }
    candidate = addDays(candidate, 1);
  }

  return startDate;
}
